import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/contexts/LitSessionProvider';
import { updateDbCloudWallet } from '@/lib/db/DBCloudWalletHelpers';
import { useUserCloudQuery } from '../onboarding/useReferralQuery';
export const useSavedToken = ({
  tokenData
}: {
  tokenData?: any;
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    PKP
  } = useSession();
  const {
    data: savedTokenData,
    isLoading: loadingSavedTokens
  } = useUserCloudQuery(PKP);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  useEffect(() => {
    if (savedTokenData?.data?.saved_tokens && router.query?.address && router.query?.networkId) {
      setIsSaved(checkIsSaved(router.query?.address, router.query?.networkId));
    }
  }, [savedTokenData, router.query]);
  const checkIsSaved = (address, networkId) => {
    const saved = savedTokenData?.data?.saved_tokens?.some(obj => {
      const tokenExist = obj.token?.toString() === address?.toString() && Number(obj.network) === Number(networkId);
      return tokenExist;
    });
    return saved;
  };
  const handleSave = async () => {
    if (!tokenData) {
      return;
    }
    const saved = savedTokenData?.data?.saved_tokens ?? [];
    if (saved.some(obj => obj.token === router.query?.address && obj.network === router.query?.networkId)) {
      setIsSaved(false);
      const updatedTokens = saved.filter(item => item.token !== router.query?.address);
      await updateDbCloudWallet({
        eth_address: PKP?.ethAddress,
        saved_tokens: JSON.stringify(updatedTokens)
      });
    } else {
      const newToken = {
        token: router.query?.address,
        network: router.query?.networkId,
        name: tokenData?.token?.name
      };
      saved.push(newToken);
      await updateDbCloudWallet({
        eth_address: PKP?.ethAddress,
        saved_tokens: JSON.stringify(saved)
      });
      setIsSaved(true);
      setIsSaveOpen(true);
    }
    queryClient.invalidateQueries({
      queryKey: ['cloudUserData']
    });
  };
  const handleSaveSingle = async ({
    tokenToSave
  }: {
    tokenToSave: any;
  }) => {
    if (!tokenToSave) {
      return;
    }
    const saved = savedTokenData?.data?.saved_tokens ?? [];
    if (saved.some(obj => obj.token === router.query?.address && obj.network === router.query?.networkId)) {
      setIsSaved(false);
      const updatedTokens = saved.filter(item => item.token !== router.query?.address);
      await updateDbCloudWallet({
        eth_address: PKP?.ethAddress,
        saved_tokens: JSON.stringify(updatedTokens)
      });
    } else {
      const newToken = {
        token: tokenToSave?.address,
        network: tokenToSave?.networkId,
        name: tokenToSave?.name
      };
      saved.push(newToken);
      await updateDbCloudWallet({
        eth_address: PKP?.ethAddress,
        saved_tokens: JSON.stringify(saved)
      });
      setIsSaved(true);
      setIsSaveOpen(true);
    }
    queryClient.invalidateQueries({
      queryKey: ['cloudUserData']
    });
  };
  const handleDeleteToken = async (tokenToDelete: any) => {
    if (!savedTokenData?.data?.saved_tokens || !tokenToDelete) {
      return;
    }
    const saved = savedTokenData?.data?.saved_tokens.filter((token: any) => token.token !== tokenToDelete.token);
    await updateDbCloudWallet({
      eth_address: PKP?.ethAddress,
      saved_tokens: JSON.stringify(saved)
    });
    queryClient.invalidateQueries({
      queryKey: ['cloudUserData']
    });
  };
  const handleDeleteTokenFromHome = async (tokenToDelete: any) => {
    if (!savedTokenData?.data?.saved_tokens || !tokenToDelete) {
      return;
    }
    const saved = savedTokenData?.data?.saved_tokens.filter((token: any) => token.token !== tokenToDelete.address);
    await updateDbCloudWallet({
      eth_address: PKP?.ethAddress,
      saved_tokens: JSON.stringify(saved)
    });
    queryClient.invalidateQueries({
      queryKey: ['cloudUserData']
    });
  };
  const handleClearSavedTokens = async () => {
    if (!savedTokenData?.data?.saved_tokens) {
      return;
    }
    await updateDbCloudWallet({
      eth_address: PKP?.ethAddress,
      saved_tokens: JSON.stringify([])
    });
    queryClient.invalidateQueries({
      queryKey: ['cloudUserData']
    });
  };
  return {
    loadingSavedTokens,
    isSaved,
    checkIsSaved,
    handleSave,
    handleSaveSingle,
    isSaveOpen,
    setIsSaveOpen,
    handleClearSavedTokens,
    handleDeleteTokenFromHome,
    handleDeleteToken,
    savedTokenData: savedTokenData?.data?.saved_tokens ? savedTokenData?.data?.saved_tokens : []
  };
};